import { Injectable } from '@angular/core';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { TLLBoardInfoActionType } from './ll-board.models';
import * as LLBoardActions from './ll-board.actions';
import { of, from } from 'rxjs';
import { switchMap, map, catchError, take, finalize, exhaustMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class LlBoardEffects {
  constructor(
    private actions$: Actions,
    private plani8DataService: Plani8DataService,
    private appNotificationUtilClass: AppNotificationUtilClass,
  ) {}

  getLLBoardList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.GetLLBoardList),
      exhaustMap((action: LLBoardActions.GetLLBoardList) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          switchMap((loaderEl) =>
            this.plani8DataService.getLLBoardList(action.payload).pipe(
              map((res) => new LLBoardActions.GetLLBoardListSuccess(res)),
              catchError(() => of(new LLBoardActions.GetLineShiftInfoFailure())),
              finalize(() => this.appNotificationUtilClass.dismissLoadingV2(loaderEl)),
            ),
          ),
        ),
      ),
    ),
  );

  getLineShiftInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.GetLineShiftInfo),
      switchMap((action: LLBoardActions.GetLineShiftInfo) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.getLineShiftInfo(action.subjectId, action.factoryId, action.shiftId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new LLBoardActions.GetLineShiftInfoSuccess(res)),
          catchError(() => {
            return of(new LLBoardActions.GetLineShiftInfoFailure());
          }),
        ),
      ),
    ),
  );

  addEditWorkingHours$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.AddEditLineWorkingHours),
      switchMap((action: LLBoardActions.AddEditLineWorkingHours) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.addEditLineWorkingHours(action.factoryId, action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            this.appNotificationUtilClass.presentToast('Edit working count successful');
            return new LLBoardActions.GetLineShiftInfo(action.subjectId, action.factoryId, action.payload.shift_id);
          }),
          catchError(() => {
            return of(new LLBoardActions.AddEditLineWorkingHoursFailure());
          }),
        ),
      ),
    ),
  );

  resetWorkingHours$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.ResetLineWorkingHours),
      switchMap((action: LLBoardActions.ResetLineWorkingHours) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.resetLineWorkingHours(action.factoryId, action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            this.appNotificationUtilClass.presentToast('Reset working hours successful');
            return new LLBoardActions.GetLineShiftInfo(action.subjectId, action.factoryId, action.payload.shift_id);
          }),
          catchError(() => {
            return of(new LLBoardActions.ResetLineWorkingHoursFailure());
          }),
        ),
      ),
    ),
  );

  addEditLineWorkForceCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.AddEditLineWorkForceCount),
      switchMap((action: LLBoardActions.AddEditLineWorkForceCount) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.addEditLineWorkForceCount(action.payload, action.factoryId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            this.appNotificationUtilClass.presentToast('Edit workforce count successful');
            return new LLBoardActions.GetLineShiftInfo(
              action.payload.subjectId,
              action.factoryId,
              action.payload.shift_id,
            );
          }),
          catchError(() => {
            return of(new LLBoardActions.AddEditLineWorkForceCountFailure());
          }),
        ),
      ),
    ),
  );

  resetLineWorkForceCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.ResetLineWorkForceCount),
      switchMap((action: LLBoardActions.ResetLineWorkForceCount) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.resetLineWorkForceCount(action.payload, action.factoryId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            this.appNotificationUtilClass.presentToast('Reset line workforce count successful');
            return new LLBoardActions.GetLineShiftInfo(
              action.payload.subjectId,
              action.factoryId,
              action.payload.shift_id,
            );
          }),
          catchError(() => {
            return of(new LLBoardActions.ResetLineWorkForceCountFailure());
          }),
        ),
      ),
    ),
  );

  getLLTaskManager$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.GetLLBoardTaskManager),
      switchMap((action: LLBoardActions.GetLLBoardTaskManager) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.getLLTaskManager().pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new LLBoardActions.GetLLBoardTaskManagerSuccess(res)),
          catchError(() => {
            return of(new LLBoardActions.GetLLBoardTaskManagerFailure());
          }),
        ),
      ),
    ),
  );

  getLearningCurveStripInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.GetLearningCurveStripeInfo),
      switchMap((action: LLBoardActions.GetLearningCurveStripeInfo) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.getLLLearningCurveInfo(action.stripId, action.shiftId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new LLBoardActions.GetLearningCurveStripeInfoSuccess(res)),
          catchError(() => of(new LLBoardActions.GetLearningCurveStripeInfoFailure())),
        ),
      ),
    ),
  );

  updateLearningCurveDayCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.UpdateLearningCurveDayCount),
      switchMap((action: LLBoardActions.UpdateLearningCurveDayCount) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.incrementLLLearningDayCount(action.stripId, action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            this.appNotificationUtilClass.presentToast('Day count update successful');
            return new LLBoardActions.GetLearningCurveStripeInfo(action.stripId, action.payload.shift_id);
          }),
          catchError(() => {
            return of(new LLBoardActions.UpdateLearningCurveDayCountFailure());
          }),
        ),
      ),
    ),
  );

  addEditLearningCurveStrip$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.AddEditLearningCurveStrip),
      switchMap((action: LLBoardActions.AddEditLearningCurveStrip) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.addEditLLLearningCurves(action.stripId, action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new LLBoardActions.GetLearningCurveStripeInfo(action.stripId, action.payload.shift_id);
          }),
          catchError(() => {
            return of(new LLBoardActions.AddEditLearningCurveStripFailure());
          }),
        ),
      ),
    ),
  );

  deleteLearningCurveStrip$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.DeleteLearningCurveStrip),
      switchMap((action: LLBoardActions.DeleteLearningCurveStrip) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.deleteLLLearningCurveStrip(action.stripId, action.shiftId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new LLBoardActions.GetLearningCurveStripeInfo(action.stripId, action.shiftId);
          }),
          catchError(() => {
            return of(new LLBoardActions.DeleteLearningCurveStripFailure());
          }),
        ),
      ),
    ),
  );

  getLearningCurveByFactoryOtl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.GetLearningCurveByFactoryOtl),
      switchMap((action: LLBoardActions.GetLearningCurveByFactoryOtl) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.getLLLearningCurveByFactoryOTL(action.factoryId, action.otlId, action.shiftId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new LLBoardActions.GetLearningCurveByFactoryOtlSuccess(res);
          }),
          catchError(() => of(new LLBoardActions.GetLearningCurveByFactoryOtlFailure())),
        ),
      ),
    ),
  );

  addEditLearningCurveByFactoryOtl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.AddEditLearningCurveByFactoryOtl),
      switchMap((action: LLBoardActions.AddEditLearningCurveByFactoryOtl) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.addEditLLLearningCurveByFactoryOTL(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new LLBoardActions.GetLearningCurveByFactoryOtl(
              action.payload.factory_id,
              action.payload.otl_id,
              action.payload.shift_id,
            );
          }),
          catchError(() => {
            return of(new LLBoardActions.AddEditLearningCurveByFactoryOtlFailure());
          }),
        ),
      ),
    ),
  );

  getLineStripListingInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.GetLineStripListing),
      switchMap((action: LLBoardActions.GetLineStripListing) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.fetchLineLevelStripListing(action.lineSubject, action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new LLBoardActions.GetLineStripListingSuccess(res)),
          catchError(() => {
            return of(new LLBoardActions.GetLineStripListingFailure());
          }),
        ),
      ),
    ),
  );

  getLineStripPlannedNumbers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.LineStripPlannedNumbers),
      switchMap((action: LLBoardActions.LineStripPlannedNumbers) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.getStripPlannedNumbers(action.stripId, action.factoryId, action.shiftId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new LLBoardActions.LineStripPlannedNumbersSuccess(res)),
          catchError(() => {
            return of(new LLBoardActions.LineStripPlannedNumbersFailure());
          }),
        ),
      ),
    ),
  );
  unloadStripMovePartially$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.UnloadStripMovePartially),
      switchMap((action: LLBoardActions.UnloadStripMovePartially) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.unloadStripMovePartially(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new LLBoardActions.UnloadStripMovePartiallySuccess()),
          catchError(() => {
            return of(new LLBoardActions.UnloadStripMovePartiallyFailure());
          }),
        ),
      ),
    ),
  );

  addEditNoteOnStrip$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.AddEditNoteOnStrip),
      switchMap((action: LLBoardActions.AddEditNoteOnStrip) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.addEditNote(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new LLBoardActions.AddEditNoteOnStripSuccess()),
          catchError(() => {
            return of(new LLBoardActions.AddEditNoteOnStripFailure());
          }),
        ),
      ),
    ),
  );
}
